import React, { MutableRefObject, createContext } from "react";
import {
  ComponentDataSource,
  DeckInfo,
  EventManager,
  FontStyleOverride,
  GameComponent,
  GameSituationBackend,
  PlayerColorInfo,
  PositionData,
} from "../types";
import { EvalClick, GameComponentForGame, GameState, PossiblyUnknownGameComponent } from "../implementationTypes";
import { GameTableLogic } from "./redesign/gameTableLogic";
import { ProjectFontDefinition } from "src/components/types";
import { FontDefinition } from "src/components/pages/FontAdmin";
import { FontStyle } from "@mui/material/styles/createTypography";

export type TableContextType = {
  eventManager: EventManager;
  isClickable: (componentData: GameComponent, relativeGameSituation?: GameSituationBackend) => boolean | string;
  evalClick: EvalClick;
  tableState: GameSituationBackend;
  workingTableStateRef: React.MutableRefObject<GameSituationBackend>;
  getPcCoords: (
    compData: GameComponent,
    positionName: string,
    positionIndex: number | string,
    positionMultiI?: number,
    allChildren?: GameComponentForGame[],
    childData?: PossiblyUnknownGameComponent,
    componentInfo?: ComponentDataSource,
  ) => number[];
  getComponentInfo: (componentData: PossiblyUnknownGameComponent) => ComponentDataSource;
  adjustedGameState: (state: GameState) => GameState;
  playerColors?: PlayerColorInfo[];
  selectedColor?: string;
  makeTurnsAs?: string;
  playAsColor?: string;
  decks: {
    [key: string]: DeckInfo;
  };
  globalAssets: {
    [key: string]: any;
  };
  switchPlayer: (color: string) => void;
  evalAutoStates: (
    gameSituation: GameSituationBackend,
    eventManager: EventManager,
    sourcePlayer?: string | undefined,
  ) => void;
  reloadComponents: () => void;
  setEditMode: (value: boolean) => void;
  evalSpeed: number | string;
  getImage: (imageName: string, cssPath?: boolean) => string | undefined;
  componentPool?: { [key: string]: ComponentDataSource };
  situationScale: number;
  clipLog?: boolean;
  editMode?: boolean;
  cheatMode?: boolean;
  skipAnimation?: () => void;
  canInvite?: boolean;
  windowWidth?: number;
  defaultSelected?: string;
  customTooltip: (componentData: GameComponentForGame) => string;
  isPosVisible?: (
    position: PositionData,
    componentData: GameComponentForGame,
    gameSituation: GameSituationBackend,
    selectedComponent: GameComponentForGame | null,
  ) => boolean | string;
  selectedComponent: GameComponentForGame | null;
  setUserSelectedComponent: (componentData: GameComponentForGame | null) => void;
  openDeckContentId: string | null;
  setOpenDeckContentId: (id: string | null) => void;
  deckContentSlotRef?: React.RefObject<HTMLDivElement>;
  mousePos?: MutableRefObject<{ x: number; y: number }>;
  toggleZoomLens: (newValue: boolean) => void;
  connectedPlayers?: Set<string>;
  tableStatus?: string;
  tableId?: string;
  projectFonts?: Array<ProjectFontDefinition>;
  deckCardFonts?: Array<FontStyleOverride>;
};

export const TableContext = createContext<TableContextType | null>(null);

export function makeTableContext(
  logic: GameTableLogic,
  evalSpeed: number,
  toggleZoomLens: (newValue: boolean) => void,
  mousePos: MutableRefObject<{ x: number; y: number }>,
  deckContent: {
    openId: string | null;
    setOpenId: (id: string | null) => void;
    slotRef: React.RefObject<HTMLDivElement>;
  },
) {
  let tableData = logic.tableData;
  let canInvite =
    tableData?.tableMode === "demo-invite" &&
    (localStorage.getItem("user-role")?.includes("admin") ||
      tableData.projectInfo.roles?.designer?.includes(localStorage.getItem("user-name") ?? "") ||
      tableData.projectInfo.roles?.mod?.includes(localStorage.getItem("user-name") ?? ""));

  let tableContextProviderValue: TableContextType = {
    eventManager: logic.eventManager,
    tableId: logic.tableId,
    isClickable: logic.isClickable,
    evalClick: logic.evalClick,
    customTooltip: logic.customTooltip,
    tableState: logic.tableStateRef.current as GameSituationBackend,
    workingTableStateRef: logic.workingTableStateRef as React.MutableRefObject<GameSituationBackend>,
    getPcCoords: logic.getPcCoords as (
      compData: GameComponentForGame,
      positionName: string,
      positionIndex: string | number,
      positionMultiI?: number,
      children?: GameComponentForGame[],
      childData?: PossiblyUnknownGameComponent,
      componentInfo?: ComponentDataSource,
    ) => number[],
    getComponentInfo: logic.getComponentInfo,
    adjustedGameState: logic.adjustGameState,
    playerColors: logic.playerColors,
    selectedColor: logic.selectedColor,
    playAsColor: logic.playAsColor,
    makeTurnsAs: logic.makeTurnsAs,
    decks: logic.decks as { [key: string]: DeckInfo },
    globalAssets: logic.tableData?.projectInfo.globalAssets ?? {},
    switchPlayer: logic.switchPlayer,
    evalAutoStates: logic.evalAutoStates,
    reloadComponents: logic.reloadComponents,
    setEditMode: logic.setEditMode,
    evalSpeed,
    toggleZoomLens,
    getImage: logic.getImage,
    isPosVisible: logic.isPosVisible,
    situationScale: logic.getTargetScale(),
    editMode: logic.editMode,
    windowWidth: logic.windowWidth,
    clipLog: logic.clipLog,
    skipAnimation: logic.skipAnimation,
    cheatMode: logic.cheatMode,
    setCheatMode: logic.setCheatMode,
    defaultSelected: logic.defaultSelected,
    canInvite: canInvite,
    selectedComponent: logic.selectedComponent,
    mousePos: mousePos,
    setUserSelectedComponent: logic.setUserSelectedComponent,
    openDeckContentId: deckContent.openId,
    setOpenDeckContentId: deckContent.setOpenId,
    deckContentSlotRef: deckContent.slotRef,
    connectedPlayers: logic.connectedPlayers,
    tableStatus: logic.tableStatus,
    projectFonts: logic.tableData?.projectInfo.projectFonts,
    deckCardFonts: logic.tableData?.projectInfo.deckCardFonts,
  };

  return tableContextProviderValue;
}
